/**
 * Historique des mots de passe d'une entree. Les anciennes valeurs vivent
 * UNIQUEMENT dans les secrets chiffres (`secrets.passwordHistory`), jamais en
 * metadonnee : elles se synchronisent et s'exportent comme le reste de
 * l'entree, ciphertext-only. Module pur, zero E/S.
 */
import type { EntrySecrets } from '@klavox/crypto-core';

/** Anciennes valeurs conservees par entree (les plus anciennes sont abandonnees au-dela). */
export const MAX_PASSWORD_HISTORY = 20;

export interface PasswordHistoryEntry {
  readonly password: string;
  /** Instant (ms) ou cette valeur a cesse d'etre le mot de passe courant. */
  readonly changedAt: number;
}

/** Historique d'une entree, plus recent d'abord ; les elements mal formes (serveur, import) sont ignores. */
export function readPasswordHistory(secrets: EntrySecrets): PasswordHistoryEntry[] {
  const raw = secrets['passwordHistory'];
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (item): item is PasswordHistoryEntry =>
        item !== null &&
        typeof item === 'object' &&
        typeof item.password === 'string' &&
        item.password.length > 0 &&
        Number.isSafeInteger(item.changedAt) &&
        item.changedAt >= 0,
    )
    .map(({ password, changedAt }) => ({ password, changedAt }))
    .sort((a, b) => b.changedAt - a.changedAt);
}

/**
 * Instant (ms) ou le mot de passe COURANT a ete pose, quand l'export d'origine
 * le date (Bitwarden `passwordRevisionDate`, creation de l'element 1Password) ;
 * `null` si absent ou mal forme. Sans lui, une entree importee semblerait dater
 * de l'import, et la fuite d'un site anterieure a l'import passerait inapercue.
 */
export function readPasswordChangedAt(secrets: EntrySecrets): number | null {
  const raw = secrets['passwordChangedAt'];
  return typeof raw === 'number' && Number.isSafeInteger(raw) && raw >= 0 ? raw : null;
}

/**
 * Dernier changement connu du mot de passe courant : le plus recent de
 * l'historique (retrait de l'ancien) et de la date importee ; `fallback`
 * (creation de l'entree) si aucun des deux n'existe. Le plus recent, jamais le
 * plus ancien : une date douteuse ne fait pas croire a un mot de passe perime.
 */
export function lastPasswordChange(secrets: EntrySecrets, fallback: number): number {
  const known = [readPasswordHistory(secrets)[0]?.changedAt, readPasswordChangedAt(secrets) ?? undefined].filter((at): at is number => at !== undefined);
  return known.length > 0 ? Math.max(...known) : fallback;
}

/**
 * Historique normalise : plus recent d'abord, sans doublon, sans la valeur
 * courante (restaurer un ancien mot de passe ne le laisse pas en « ancien »),
 * plafonne a `MAX_PASSWORD_HISTORY`.
 */
export function normalizePasswordHistory(history: readonly PasswordHistoryEntry[], currentPassword: string): PasswordHistoryEntry[] {
  const seen = new Set([currentPassword]);
  return readPasswordHistory({ passwordHistory: history })
    .filter(({ password }) => !seen.has(password) && seen.add(password))
    .slice(0, MAX_PASSWORD_HISTORY);
}

/**
 * Secrets a enregistrer lors d'une modification : l'historique est repris de
 * `next` s'il le fournit explicitement (effacement, import), sinon de
 * `previous` ; le mot de passe remplace y est ajoute s'il a change, et la
 * date importee du mot de passe (`passwordChangedAt`) est alors retiree.
 */
export function withPasswordHistory(previous: EntrySecrets, next: EntrySecrets, now: number): EntrySecrets {
  const base = readPasswordHistory(Array.isArray(next['passwordHistory']) ? next : previous);
  const oldPassword = typeof previous.password === 'string' ? previous.password : '';
  const newPassword = typeof next.password === 'string' ? next.password : '';
  const history = normalizePasswordHistory(
    oldPassword.length > 0 && oldPassword !== newPassword ? [{ password: oldPassword, changedAt: now }, ...base] : base,
    newPassword,
  );
  const { passwordHistory: _dropped, ...kept } = next;
  // Le mot de passe change : la date importee ne decrit plus le mot de passe courant, l'historique prend le relais.
  const { passwordChangedAt: _stale, ...changed } = kept;
  const rest = oldPassword.length > 0 && oldPassword !== newPassword ? changed : kept;
  return history.length > 0 ? { ...rest, passwordHistory: history } : rest;
}
